import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    POSConfig,
    DEFAULT_POS_CONFIG,
    PosConfigLevel,
    PosProjectRow,
    validatePosConfig,
} from "./constants";
import {
    fetchPosGeneralSettings,
    fetchPosItemsDisable,
    PosItemDisable,
    savePosConfigToServer,
} from "./posConfigService";

// ─── Helpers ───

/**
 * Set a value deep in a nested object using a string path.
 * Creates intermediate objects as needed. Used by the updateConfig
 * reducer to mutate POSConfig via dynamic paths — the cast to a
 * record of unknown values is localized here so the reducer body
 * and callers remain free of type assertions.
 */
function setNestedValue(
    target: object,
    path: readonly string[],
    value: unknown
): void {
    let current = target as Record<string, unknown>;
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key]) {
            current[key] = {};
        }
        current = current[key] as Record<string, unknown>;
    }
    current[path[path.length - 1]] = value;
}

// ─── Types ───

interface PosConfigState {
    config: POSConfig;
    configSnapshot: POSConfig | null;
    isModalOpened: boolean;
    isLoading: boolean;
    isSaving: boolean;
    isConsultation: boolean;
    showPropagationConfirm: boolean;
    selectedRow: PosProjectRow | null;
    configLevel: PosConfigLevel;
    franchiseId: string;
    shopId: string;
    userId: string;
    operatorId: string;
    errorMessage: string | null;
    /**
     * Items deactivated in general settings (sale modes, payment methods,
     * languages, information modes). Each entry carries the originating
     * shopId; subcomponents grey out cards whose id+scope matches AND
     * whose template status is "en attente".
     */
    itemsDisable: PosItemDisable[];
    /**
     * Monotonic counter bumped after every successful persistence.
     * Table/list components subscribe via `useSelector` and refetch
     * data whenever the value changes. Replaces the previous module-level
     * singleton callback.
     */
    dataUpdatedTrigger: number;
}

interface OpenModalPayload {
    row: PosProjectRow;
    level: PosConfigLevel;
    franchiseId?: string;
    shopId?: string;
    userId?: string;
    operatorId?: string;
    isConsultation?: boolean;
}

interface SaveResult {
    success: boolean;
    errors: string[];
    needsConfirmation?: boolean;
}

// ─── Initial State ───

const initialState: PosConfigState = {
    config: { ...DEFAULT_POS_CONFIG },
    configSnapshot: null,
    isModalOpened: false,
    isLoading: false,
    isSaving: false,
    isConsultation: false,
    showPropagationConfirm: false,
    selectedRow: null,
    configLevel: "franchise",
    franchiseId: "",
    operatorId: "",
    shopId: "",
    userId: "",
    errorMessage: null,
    itemsDisable: [],
    dataUpdatedTrigger: 0,
};

// ─── Helpers ───

function deepCopy<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

export function hasSubLevelCustomConfigs(
    subRows: PosProjectRow[] | null | undefined,
    targetLevel: PosConfigLevel
): boolean {
    if (!subRows || subRows.length === 0) return false;
    for (const subRow of subRows) {
        const tmpl = subRow?.template;
        if (
            tmpl &&
            typeof tmpl === "object" &&
            Object.keys(tmpl).length > 0
        ) {
            return true;
        }
        if (targetLevel === "shop" && subRow?.subRows) {
            if (hasSubLevelCustomConfigs(subRow.subRows, "application")) {
                return true;
            }
        }
    }
    return false;
}

function computeHasDiff(
    oldConfig: POSConfig,
    newConfig: POSConfig
): boolean {
    return JSON.stringify(oldConfig) !== JSON.stringify(newConfig);
}

// ─── Helpers: ID resolution ───

function resolveProjectId(row: PosProjectRow | null): string {
    return (row?.projectId || row?.ProjectId || row?.key_project || "") as string;
}

function resolveShopId(
    state: PosConfigState
): string {
    const { selectedRow, configLevel, shopId } = state;
    if (configLevel === "franchise") return "0";
    return (
        (selectedRow?.id_boutique as string) ||
        (selectedRow?.shopId as string) ||
        shopId ||
        "0"
    );
}

async function persistConfig(state: PosConfigState, propagateToChildren: boolean): Promise<void> {
    const uuidUser = localStorage.getItem("uuidUser");
    if (!uuidUser) {
        throw new Error(
            "Session expired: missing user identifier. Please sign in again."
        );
    }
    const configCopy = deepCopy(state.config);
    await savePosConfigToServer({
        userId: state.userId,
        franchiseId: state.franchiseId,
        shopId: resolveShopId(state),
        projectId: resolveProjectId(state.selectedRow),
        config: configCopy,
        configLevel: state.configLevel,
        propagateToChildren,
        appId: (state.selectedRow?.appId as string) || "",
        keyApplication: (state.selectedRow?.key_application as string) || "",
        operatorId: state.operatorId,
        uuidUser,
    });
}

// ─── Async Thunks ───

export const openPosConfigModal = createAsyncThunk<
    { config: POSConfig; itemsDisable: PosItemDisable[] },
    OpenModalPayload
>("posConfig/openModal", async (payload) => {
    const { row, level, franchiseId, shopId, userId } = payload;

    let apiShopId = "0";
    if (level === "shop" || level === "application") {
        apiShopId =
            (row.id_boutique as string) ||
            (row.shopId as string) ||
            shopId ||
            "0";
    }
    const apiFranchiseId =
        franchiseId ||
        row.projectId ||
        row.ProjectId ||
        "";

    const itemsDisable = await fetchPosItemsDisable(apiFranchiseId, apiShopId);

    const rowTemplate = row?.template;
    const hasExistingTemplate =
        rowTemplate &&
        typeof rowTemplate === "object" &&
        Object.keys(rowTemplate).length > 0;

    if (hasExistingTemplate) {
        const tmpl = rowTemplate as POSConfig;
        return {
            config: {
                SalesMode: tmpl.SalesMode || DEFAULT_POS_CONFIG.SalesMode,
                PaymentMode:
                    tmpl.PaymentMode || DEFAULT_POS_CONFIG.PaymentMode,
                Language: tmpl.Language || DEFAULT_POS_CONFIG.Language,
                InformationMode:
                    tmpl.InformationMode ||
                    DEFAULT_POS_CONFIG.InformationMode,
                PaperlessTicket:
                    tmpl.PaperlessTicket ||
                    DEFAULT_POS_CONFIG.PaperlessTicket,
            },
            itemsDisable,
        };
    }

    if (apiFranchiseId) {
        const apiConfig = await fetchPosGeneralSettings(
            apiFranchiseId,
            apiShopId,
            userId
        );
        return { config: apiConfig, itemsDisable };
    }

    return { config: deepCopy(DEFAULT_POS_CONFIG), itemsDisable };
});

export const savePosConfig = createAsyncThunk<
    SaveResult,
    { propagateToChildren: boolean; t?: (key: string) => string },
    { state: { posConfig: PosConfigState } }
>("posConfig/save", async ({ propagateToChildren, t }, { getState }) => {
    const state = getState().posConfig;
    const { config, configSnapshot, selectedRow, configLevel } = state;

    const validation = validatePosConfig(config, t);
    if (!validation.isValid) {
        return { success: false, errors: validation.errors };
    }

    if (configSnapshot && !propagateToChildren) {
        if (!computeHasDiff(configSnapshot, config)) {
            return { success: true, errors: [] };
        }

        const subRows = selectedRow?.subRows || [];
        const targetLevel: PosConfigLevel =
            configLevel === "franchise" ? "shop" : "application";
        const hasCustomChildren =
            (configLevel === "franchise" || configLevel === "shop") &&
            hasSubLevelCustomConfigs(subRows, targetLevel);

        if (hasCustomChildren) {
            return { success: false, errors: [], needsConfirmation: true };
        }
    }

    await persistConfig(state, propagateToChildren);
    return { success: true, errors: [] };
});

export const persistCurrentLevelOnly = createAsyncThunk<
    SaveResult,
    void,
    { state: { posConfig: PosConfigState } }
>("posConfig/persistCurrentLevelOnly", async (_, { getState }) => {
    await persistConfig(getState().posConfig, false);
    return { success: true, errors: [] };
});

// ─── Slice ───

const posConfigSlice = createSlice({
    name: "posConfig",
    initialState,
    reducers: {
        setIsModalOpened(state, action: PayloadAction<boolean>) {
            state.isModalOpened = action.payload;
        },
        setShowPropagationConfirm(state, action: PayloadAction<boolean>) {
            state.showPropagationConfirm = action.payload;
        },
        setConfigLevel(state, action: PayloadAction<PosConfigLevel>) {
            state.configLevel = action.payload;
        },
        setSelectedRow(state, action: PayloadAction<PosProjectRow | null>) {
            state.selectedRow = action.payload;
        },
        updateConfig(
            state,
            action: PayloadAction<{ path: string[]; value: unknown }>
        ) {
            const { path, value } = action.payload;
            setNestedValue(state.config, path, value);
        },
        setConfig(state, action: PayloadAction<POSConfig>) {
            state.config = action.payload;
        },
        clearErrorMessage(state) {
            state.errorMessage = null;
        },
        resetPosConfig() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        // openPosConfigModal
        builder
            .addCase(openPosConfigModal.pending, (state, action) => {
                const { row, level, franchiseId, shopId, userId, operatorId, isConsultation } =
                    action.meta.arg;
                state.selectedRow = row;
                state.configLevel = level;
                state.isModalOpened = true;
                state.isLoading = true;
                state.isConsultation = isConsultation || false;
                state.franchiseId = franchiseId || "";
                state.shopId = shopId || "";
                state.userId = userId || "";
                state.operatorId = operatorId || "";
                state.errorMessage = null;
            })
            .addCase(openPosConfigModal.fulfilled, (state, action) => {
                state.config = action.payload.config;
                state.configSnapshot = deepCopy(action.payload.config);
                state.itemsDisable = action.payload.itemsDisable;
                state.isLoading = false;
                state.errorMessage = null;
            })
            .addCase(openPosConfigModal.rejected, (state, action) => {
                state.config = deepCopy(DEFAULT_POS_CONFIG);
                state.configSnapshot = deepCopy(DEFAULT_POS_CONFIG);
                state.itemsDisable = [];
                state.isLoading = false;
                state.errorMessage =
                    action.error.message ||
                    "Failed to load POS configuration";
            });

        // savePosConfig
        builder
            .addCase(savePosConfig.pending, (state) => {
                state.isSaving = true;
            })
            .addCase(savePosConfig.fulfilled, (state, action) => {
                state.isSaving = false;
                if (action.payload.needsConfirmation) {
                    state.showPropagationConfirm = true;
                } else if (action.payload.success) {
                    // Successful save with no confirmation needed:
                    // signal listeners to refresh the underlying table.
                    state.dataUpdatedTrigger += 1;
                }
            })
            .addCase(savePosConfig.rejected, (state) => {
                state.isSaving = false;
            });

        // persistCurrentLevelOnly
        builder
            .addCase(persistCurrentLevelOnly.pending, (state) => {
                state.isSaving = true;
            })
            .addCase(persistCurrentLevelOnly.fulfilled, (state) => {
                state.isSaving = false;
                state.dataUpdatedTrigger += 1;
            })
            .addCase(persistCurrentLevelOnly.rejected, (state) => {
                state.isSaving = false;
            });
    },
});

export const {
    setIsModalOpened,
    setShowPropagationConfirm,
    setConfigLevel,
    setSelectedRow,
    updateConfig,
    setConfig,
    clearErrorMessage,
    resetPosConfig,
} = posConfigSlice.actions;

export default posConfigSlice.reducer;
